import * as countdown from 'countdown';

// tslint:disable:no-bitwise the 3rd party countdown tool uses bitwise operations in an acceptable manner

export default class DateTimeUtils {

  public static MILLISECONDS_IN_A_MINUTE = 1000 * 60;

  // output: HH:mm:ss
  // Hours can be 24+
  public static durationAsHoursMinutesSecondsString(startDateTime: Date, endDateTime: Date): string {
    const duration = countdown(startDateTime, endDateTime, countdown.HOURS | countdown.MINUTES | countdown.SECONDS);

    const hours = this.padZeroes(duration['hours']);
    const minutes = this.padZeroes(duration['minutes']);
    const seconds = this.padZeroes(duration['seconds']);

    return `${hours}:${minutes}:${seconds}`;
  }

  private static padZeroes(n: number) { return n < 10 ? '0' + n : n; }

  public static durationAsDaysOrHoursOrMinutesObject(startDateTime: Date, endDateTime: Date): any {
    // include seconds in calculation or it'll round up 30secs to the next minute
    const duration = countdown(startDateTime, endDateTime, countdown.DAYS | countdown.HOURS | countdown.MINUTES | countdown.SECONDS);

    const days = duration['days'];
    const hours = duration['hours'];
    const minutes = duration['minutes'];
    const isNegative = duration['value'] < 0;

    let response = {
      unit: 'min',
      unitCode: 'm',
      value: minutes
    };

    if (days > 0) {
      response = {
        unit: 'day',
        unitCode: 'd',
        value: days
      };
    } else if (hours > 0) {
      response = {
        unit: 'hour',
        unitCode: 'h',
        value: hours
      };
    }

    if (response.value !== 1) {
      response.unit += 's';
    }

    response.value = isNegative ? -1 * response.value : response.value;

    return response;
  }

  // Convert a date object which is treated as 'yyyy-mm-dd' ignoring the time section to a Date object - without local timezone affecting it.
  // Prevents out by one day problem where user select 10th and 9th gets saved in db.
  //
  // Use this for 'absolute' dates such as birthdays and expiry dates input by the user - if something expires on the 10th, it should expiry
  // on the 10th relative in local time everywhere across the world; not when it is midnight in UTC.
  //
  // see: documentation/Timezones.md
  public static timelessDateAsUTCDate(date: Date): Date {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  }

  // Convert a date string 'yyyy-mm-dd' with no time information to a Date object without local timezone affecting it.
  // Prevents out by one day problem where the string says the 10th, but the code assumes UTC and changes it to the 9th for local time.
  //
  // Use this when editing an existing 'absolute' date such as birthdays or expiry dates in a user input form.
  //
  // see: documentation/Timezones.md
  public static timelessDateStrAsUTCDate(dateStr: string): Date {
    return new Date(parseInt(dateStr.slice(0, 4), 10), parseInt(dateStr.slice(5, 7), 10) - 1, parseInt(dateStr.slice(8, 10), 10));
  }

  public static utcDateToTimelessString(date: Date): String {
    return date.toISOString().split('T')[0];
  }

  public static localDateToTimelessString(date: Date): string {
    return this.timelessDateAsUTCDate(date).toISOString().split('T')[0];
  }

  public static isDateStrExpired(date: string): boolean {
    const expiryDate = new Date(date);
    return DateTimeUtils.isDateExpired(expiryDate);
  }

  public static isDateExpired(date: Date): boolean {
    const today = DateTimeUtils.timelessDateAsUTCDate(new Date());

    return date < today;
  }

  // Check if a timeless date string 'yyyy-mm-dd' falls on or before the user's local calendar day `days` from today.
  // Timeless dates are local business dates, so they are compared as calendar days in the user's locale, See documentation/Timezones.md
  public static isDateStrWithinDays(dateStr: string, days: number): boolean {
    const cutoffDay = new Date();
    cutoffDay.setDate(cutoffDay.getDate() + days);

    return dateStr <= DateTimeUtils.localDateToTimelessString(cutoffDay);
  }
}
